import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import SeoImage from "@/components/SeoImage";
import { treatmentJsonLd, breadcrumbJsonLd } from "@/lib/schema";

export default function TreatmentDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/treatments/${slug}`).then((r) => setT(r.data)).catch(() => setErr("No encontrado"));
  }, [slug]);

  if (err) return <div className="mx-auto max-w-3xl px-6 py-24">Tratamiento no encontrado.</div>;
  if (!t) return <div className="mx-auto max-w-3xl px-6 py-24">Cargando…</div>;

  return (
    <article data-testid={`treatment-detail-${slug}`} className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
      <SEOHead
        title={`${t.name} en Armenia, Quindío · Katia Falón Odontología`}
        description={`${t.name} en Armenia, Quindío. ${t.description}`.slice(0, 300)}
        path={`/tratamientos/${t.slug}`}
        jsonLd={[treatmentJsonLd(t), breadcrumbJsonLd([{name:"Inicio",path:"/"},{name:"Tratamientos",path:"/tratamientos"},{name:t.name,path:`/tratamientos/${t.slug}`}])]}
      />
      <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">Tratamiento</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">{t.name}</h1>
          <p className="mt-4 text-lg text-[hsl(var(--clinic-secondary))]">{t.tagline}</p>
          <p className="mt-6 leading-relaxed text-[hsl(var(--clinic-muted))]">{t.description}</p>
          <div className="mt-8 rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-6">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--clinic-muted))]">Qué incluye</div>
            <ul className="mt-4 space-y-2 text-sm">
              {["Valoración diagnóstica personalizada",
                "Plan de tratamiento por escrito",
                "Bioseguridad y esterilización certificada",
                "Seguimiento post-tratamiento por WhatsApp",
              ].map((li) => (
                <li key={li} className="flex items-start gap-2"><Check size={16} className="mt-0.5 text-[hsl(var(--clinic-primary))]" />{li}</li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-[hsl(var(--clinic-muted))]">
              Detallamos el plan exacto durante tu valoración presencial.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/reservar/${t.slug}`}
              data-testid="treatment-book-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--clinic-secondary))] px-6 py-3 text-sm font-semibold text-white hover:-translate-y-0.5"
            >
              Reservar {t.name} <ArrowRight size={16} />
            </Link>
            <Link to="/contacto" className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--clinic-text))] px-6 py-3 text-sm font-semibold">
              Prefiero preguntar antes
            </Link>
          </div>
        </div>
        <div>
          {t.banner_slug ? (
            <SeoImage
              slug={t.banner_slug}
              alt={`${t.name} — ${t.tagline} · Katia Falón Odontología, Armenia (Quindío)`}
              caption={`${t.name} en Armenia, Quindío`}
              schema
              className="h-auto w-full rounded-3xl object-cover"
              testId={`treatment-detail-banner-${slug}`}
            />
          ) : (
            <div className="aspect-square w-full rounded-3xl bg-gradient-to-br from-[hsl(var(--clinic-primary))]/20 to-[hsl(var(--clinic-secondary))]/10" />
          )}
          <div className="mt-4 rounded-2xl bg-[hsl(var(--clinic-bg))] p-4 text-sm">
            <span className="font-semibold">Duración: </span>{t.duration}
          </div>
        </div>
      </div>
    </article>
  );
}
